import React, { useState, useEffect, useCallback } from 'react';
import { X, Settings, Clock, Palette, Save, Database, Radio, Zap, Brain, Users, Mic, Globe, AlertTriangle, Ban, VolumeX, FileText, ChevronDown, ChevronRight, RefreshCw, Terminal } from 'lucide-react';
import GeminiSubscriptionManager from './GeminiSubscriptionManager';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, query, orderBy, limit, getDoc, collectionGroup } from 'firebase/firestore';

export interface DataSources {
  masterEnabled: boolean;
  openmhz: boolean;       // TPD Calls for Service feed
  geminiSearch: boolean;  // AI Gemini web-search for incidents
  userReports: boolean;   // Crowd-sourced user tap reports
  microphone: boolean;    // Manual mic recording + transcription
}

export interface DevSettings {
  syncInterval: number;
  vicinityCooldown: number;
  summarizeCooldown: number;
  sources: DataSources;
  theme: {
    primary: string;
    danger: string;
    bg: string;
    header: string;
    border: string;
    accent: string;
  };
}

export const DEFAULT_SOURCES: DataSources = {
  masterEnabled: true,
  openmhz: true,
  geminiSearch: false,
  userReports: true,
  microphone: true,
};

interface UserRecord {
  uid: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  lastLogin?: number;
  banned?: boolean;
  silenced?: boolean;
  banReason?: string;
  incidentCount?: number;
}

interface DevToolsProps {
  settings: DevSettings;
  onUpdate: (settings: DevSettings) => void;
  onClose: () => void;
  userGeminiKey: string;
  setUserGeminiKey: (key: string) => void;
  isPaidSubscriber: boolean;
  setIsPaidSubscriber: (isPaid: boolean) => void;
  currentUserEmail?: string | null;
  scannerConnected?: boolean;
  callCount?: number;
  currentTier?: string;
  onTierChange?: (tier: any) => void;
}

// Reusable toggle switch
function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      style={{
        width: 36, height: 20, borderRadius: 10,
        background: on ? '#00e5ff' : '#1a1a2e',
        border: `1px solid ${on ? '#00e5ff' : '#333'}`,
        position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.18s', flexShrink: 0,
        boxShadow: on ? '0 0 8px rgba(0,229,255,0.4)' : 'none',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 2,
        left: on ? 17 : 2,
        width: 14, height: 14, borderRadius: '50%',
        background: on ? '#000' : '#444',
        transition: 'left 0.18s',
      }} />
    </button>
  );
}

// Source row
function SourceRow({
  icon, label, desc, badge, enabled, onChange, masterOff, statusDot
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
  badge?: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
  masterOff?: boolean;
  statusDot?: 'live' | 'idle' | 'off';
}) {
  const active = enabled && !masterOff;
  const dotColor = statusDot === 'live' ? '#00e5ff' : statusDot === 'idle' ? '#ffcc00' : '#333';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 14px',
      background: active ? 'rgba(0,229,255,0.04)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${active ? 'rgba(0,229,255,0.15)' : 'rgba(255,255,255,0.05)'}`,
      borderRadius: 6, marginBottom: 6,
      opacity: masterOff ? 0.45 : 1,
      transition: 'all 0.2s',
    }}>
      <div style={{ color: active ? '#00e5ff' : '#444', marginTop: 1, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{
            color: active ? '#e0e0e0' : '#555',
            fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
            fontFamily: '"Orbitron", monospace',
          }}>
            {label}
          </span>
          {badge && (
            <span style={{
              background: active ? 'rgba(0,229,255,0.15)' : 'rgba(255,255,255,0.06)',
              color: active ? '#00e5ff' : '#444',
              fontSize: 7, padding: '1px 5px', borderRadius: 3, letterSpacing: 1,
              fontFamily: '"Orbitron", monospace',
            }}>
              {badge}
            </span>
          )}
          {statusDot && (
            <div style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: dotColor,
                boxShadow: active && statusDot === 'live' ? `0 0 6px ${dotColor}` : 'none',
                animation: active && statusDot === 'live' ? 'dev-pulse 1.5s infinite' : 'none',
              }} />
              <span style={{ color: dotColor, fontSize: 7, letterSpacing: 1 }}>
                {active ? statusDot.toUpperCase() : 'OFF'}
              </span>
            </div>
          )}
        </div>
        <div style={{
          color: '#444', fontSize: 8.5, lineHeight: 1.5,
          fontFamily: '"JetBrains Mono", monospace',
        }}>
          {desc}
        </div>
      </div>
      <Toggle on={enabled} onChange={onChange} disabled={masterOff} />
    </div>
  );
}

const ADMIN_PASSWORD = 'Arya';

export default function DevTools({
  settings, onUpdate, onClose,
  userGeminiKey, setUserGeminiKey,
  isPaidSubscriber, setIsPaidSubscriber,
  currentUserEmail,
  scannerConnected, callCount,
  currentTier, onTierChange,
}: DevToolsProps) {
  const isAdmin = currentUserEmail === 'desertshibari@gmail.com';
  const [activeTab, setActiveTab] = useState<'sources' | 'users' | 'timing' | 'theme' | 'keys'>('sources');

  // ── Password gate ─────────────────────────────────────────────────
  const [unlocked, setUnlocked] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState(false);

  // ── User management state ─────────────────────────────────────────
  const [userList, setUserList] = useState<UserRecord[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState<string | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userIncidents, setUserIncidents] = useState<Record<string, any[]>>({});
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [rulesDeployed, setRulesDeployed] = useState<boolean | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    setUserLoading(true);
    setUserError(null);
    try {
      const snap = await getDocs(query(collection(db, 'users'), orderBy('lastLogin', 'desc'), limit(100)));
      const users: UserRecord[] = snap.docs.map(d => ({ uid: d.id, ...d.data() } as UserRecord));
      // Fetch incident counts
      const withCounts = await Promise.all(users.map(async u => {
        try {
          const incSnap = await getDocs(query(collection(db, 'users', u.uid, 'incidents'), limit(100)));
          return { ...u, incidentCount: incSnap.size };
        } catch { return u; }
      }));
      setUserList(withCounts);
      setRulesDeployed(true);
    } catch (err: any) {
      if (err.code === 'permission-denied') {
        setRulesDeployed(false);
        setUserError('RULES_NOT_DEPLOYED');
      } else {
        setUserError(err.message);
      }
    } finally {
      setUserLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === 'users' && isAdmin) fetchUsers();
  }, [activeTab, isAdmin, fetchUsers]);

  const loadUserIncidents = async (uid: string) => {
    if (userIncidents[uid]) return;
    try {
      const snap = await getDocs(query(collection(db, 'users', uid, 'incidents'), orderBy('timestamp', 'desc'), limit(20)));
      setUserIncidents(prev => ({ ...prev, [uid]: snap.docs.map(d => d.data()) }));
    } catch {}
  };

  const toggleExpand = (uid: string) => {
    if (expandedUser === uid) { setExpandedUser(null); return; }
    setExpandedUser(uid);
    loadUserIncidents(uid);
  };

  // ── Whitelist: grant a tier to an email you know ──────────────────────────
  const [whitelistEmail, setWhitelistEmail]   = useState('');
  const [whitelistTier, setWhitelistTier]     = useState<'spotter' | 'tactical' | 'ghost'>('spotter');
  const [whitelistStatus, setWhitelistStatus] = useState<string | null>(null);
  const [whitelistPending, setWhitelistPending] = useState(false);

  const grantWhitelist = async () => {
    const email = whitelistEmail.trim().toLowerCase();
    if (!email || !isAdmin) return;
    setWhitelistPending(true);
    setWhitelistStatus(null);
    try {
      // Find user by email in the users collection
      const snap = await getDocs(query(collection(db, 'users')));
      const match = snap.docs.find(d => (d.data().email || '').toLowerCase() === email);
      if (!match) {
        setWhitelistStatus(`❌ No account found for ${email}. They need to log in first.`);
        return;
      }
      await updateDoc(doc(db, 'users', match.id), {
        subscription: {
          tier: whitelistTier,
          subscriptionId: `whitelist-${Date.now()}`,
          activatedAt: Date.now(),
          whitelisted: true,
          grantedBy: currentUserEmail,
        },
        whitelisted: true,
      });
      setWhitelistStatus(`✅ Granted ${whitelistTier.toUpperCase()} to ${email}`);
      setWhitelistEmail('');
      fetchUsers();
    } catch (err: any) {
      setWhitelistStatus(`❌ Error: ${err.message}`);
    } finally {
      setWhitelistPending(false);
    }
  };

  const moderateUser = async (uid: string, action: 'ban' | 'unban' | 'silence' | 'unsilence') => {
    setActionPending(uid + action);
    try {
      const updates: any = {};
      if (action === 'ban')      { updates.banned = true;   updates.banReason = 'Admin action'; }
      if (action === 'unban')    { updates.banned = false;  updates.banReason = null; }
      if (action === 'silence')  { updates.silenced = true; }
      if (action === 'unsilence'){ updates.silenced = false; }
      await updateDoc(doc(db, 'users', uid), updates);
      setUserList(prev => prev.map(u => u.uid === uid ? { ...u, ...updates } : u));
    } catch (err: any) {
      setUserError(err.message);
    } finally {
      setActionPending(null);
    }
  };

  const set = (path: string, value: any) => {
    const next = JSON.parse(JSON.stringify(settings));
    const keys = path.split('.');
    let cur: any = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    onUpdate(next);
  };

  const setSrc = (key: keyof DataSources, value: boolean) => set(`sources.${key}`, value);
  const src = settings.sources ?? DEFAULT_SOURCES;
  const masterOff = !src.masterEnabled;

  const tabs = [
    { id: 'sources', label: 'SOURCES', icon: <Database size={12} /> },
    { id: 'users',   label: 'USERS',   icon: <Users size={12} />,  adminOnly: true },
    { id: 'timing',  label: 'TIMING',  icon: <Clock size={12} /> },
    { id: 'theme',   label: 'THEME',   icon: <Palette size={12} /> },
    { id: 'keys',    label: 'API KEYS', icon: <Zap size={12} /> },
  ] as const;

  // ── Password gate screen ──────────────────────────────────────────
  if (!unlocked) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(16px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '"Orbitron", monospace',
      }}>
        <div style={{
          width: 300, padding: 32,
          background: '#040108',
          border: `1px solid ${pwError ? 'rgba(255,34,68,0.5)' : 'rgba(0,229,255,0.2)'}`,
          borderRadius: 10,
          boxShadow: pwError ? '0 0 30px rgba(255,34,68,0.1)' : '0 0 30px rgba(0,229,255,0.06)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
          transition: 'all 0.2s',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🐕</div>
            <div style={{ color: '#00e5ff', fontSize: 10, fontWeight: 700, letterSpacing: 3 }}>
              ADMIN PANEL
            </div>
            <div style={{ color: '#333', fontSize: 8, marginTop: 4, letterSpacing: 1 }}>
              AUTHORIZATION REQUIRED
            </div>
          </div>

          <form
            onSubmit={e => {
              e.preventDefault();
              if (pwInput === ADMIN_PASSWORD) {
                setUnlocked(true);
                setPwError(false);
              } else {
                setPwError(true);
                setPwInput('');
                setTimeout(() => setPwError(false), 1200);
              }
            }}
            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <input
              autoFocus
              type="password"
              placeholder="Password"
              value={pwInput}
              onChange={e => { setPwInput(e.target.value); setPwError(false); }}
              style={{
                width: '100%', padding: '10px 14px',
                background: '#0a0a1a',
                border: `1px solid ${pwError ? 'rgba(255,34,68,0.6)' : 'rgba(0,229,255,0.2)'}`,
                borderRadius: 5, color: '#fff',
                fontSize: 14, fontFamily: '"JetBrains Mono", monospace',
                outline: 'none', textAlign: 'center', letterSpacing: 4,
                boxSizing: 'border-box',
                animation: pwError ? 'pw-shake 0.3s ease' : 'none',
              }}
            />
            {pwError && (
              <div style={{ color: '#ff2244', fontSize: 8, textAlign: 'center', letterSpacing: 1.5 }}>
                ACCESS DENIED
              </div>
            )}
            <button
              type="submit"
              style={{
                padding: '10px',
                background: 'rgba(0,229,255,0.1)',
                border: '1px solid rgba(0,229,255,0.3)',
                borderRadius: 5, color: '#00e5ff',
                fontSize: 9, fontWeight: 700, letterSpacing: 2,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              AUTHENTICATE
            </button>
          </form>

          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#333', fontSize: 8, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 1 }}
          >
            CANCEL
          </button>
        </div>

        <style>{`
          @keyframes pw-shake {
            0%,100%{transform:translateX(0)}
            20%{transform:translateX(-6px)}
            40%{transform:translateX(6px)}
            60%{transform:translateX(-4px)}
            80%{transform:translateX(4px)}
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        width: '100%', maxWidth: 480, maxHeight: '90vh',
        background: '#040108',
        border: '1px solid rgba(0,229,255,0.2)',
        borderRadius: 10,
        boxShadow: '0 0 40px rgba(0,229,255,0.08)',
        display: 'flex', flexDirection: 'column',
        fontFamily: '"Orbitron", monospace',
        overflow: 'hidden',
      }}>

        {/* ── Header ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px',
          borderBottom: '1px solid rgba(0,229,255,0.1)',
          background: 'rgba(0,229,255,0.04)',
        }}>
          <Settings size={15} style={{ color: '#00e5ff' }} />
          <span style={{ color: '#00e5ff', fontSize: 10, fontWeight: 700, letterSpacing: 2.5 }}>
            ADMIN CONTROL PANEL
          </span>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: '#444', cursor: 'pointer', padding: 4,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Tab bar ────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.05)',
          background: 'rgba(0,0,0,0.3)',
        }}>
          {tabs.filter(t => !('adminOnly' in t) || isAdmin).map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as any)}
              style={{
                flex: 1, padding: '9px 4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                background: activeTab === t.id ? 'rgba(0,229,255,0.08)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === t.id ? '2px solid #00e5ff' : '2px solid transparent',
                color: activeTab === t.id ? '#00e5ff' : '#333',
                fontSize: 8, letterSpacing: 1.5, cursor: 'pointer',
                transition: 'all 0.15s', fontFamily: 'inherit', fontWeight: 700,
              }}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px' }}>

          {/* ══ SOURCES TAB ══════════════════════════════════════ */}
          {activeTab === 'sources' && (
            <div>
              {/* Master switch */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px',
                background: src.masterEnabled
                  ? 'rgba(0,229,255,0.06)'
                  : 'rgba(255,34,68,0.06)',
                border: `1px solid ${src.masterEnabled ? 'rgba(0,229,255,0.25)' : 'rgba(255,34,68,0.25)'}`,
                borderRadius: 7, marginBottom: 14,
              }}>
                <div>
                  <div style={{ color: src.masterEnabled ? '#00e5ff' : '#ff2244', fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 2 }}>
                    {src.masterEnabled ? '⬤ ALL SOURCES ACTIVE' : '⬤ ALL SOURCES DISABLED'}
                  </div>
                  <div style={{ color: '#444', fontSize: 8, letterSpacing: 0.5, fontFamily: '"JetBrains Mono", monospace' }}>
                    Master kill switch — overrides all individual toggles
                  </div>
                </div>
                <Toggle on={src.masterEnabled} onChange={v => setSrc('masterEnabled', v)} />
              </div>

              {/* Source rows */}
              <SourceRow
                icon={<Radio size={15} />}
                label="TPD CALLS FOR SERVICE"
                badge="30-MIN DELAY"
                desc={`Real TPD incident reports from Tucson GIS. Geocoded and mapped automatically. ${callCount ? `${callCount} calls this session.` : 'Refreshes every 5 min.'}`}
                enabled={src.openmhz}
                onChange={v => setSrc('openmhz', v)}
                masterOff={masterOff}
                statusDot={scannerConnected ? 'live' : 'idle'}
              />

              <SourceRow
                icon={<Brain size={15} />}
                label="GEMINI AI SEARCH"
                badge="SLOW"
                desc="Queries Gemini 2.5 Flash with Google Search grounding to find active incidents near your location. Burns API quota. Use sparingly — 4 min cooldown."
                enabled={src.geminiSearch}
                onChange={v => setSrc('geminiSearch', v)}
                masterOff={masterOff}
                statusDot={src.geminiSearch ? 'idle' : 'off'}
              />

              <SourceRow
                icon={<Users size={15} />}
                label="USER REPORTS"
                badge="CROWDSOURCE"
                desc="Accepts incident data tapped by users on the map. Pins drop as USER_REPORT source incidents."
                enabled={src.userReports}
                onChange={v => setSrc('userReports', v)}
                masterOff={masterOff}
              />

              <SourceRow
                icon={<Mic size={15} />}
                label="MICROPHONE INPUT"
                badge="MANUAL"
                desc="Record local audio and transcribe via Gemini for manual incident entry. Requires microphone permission."
                enabled={src.microphone}
                onChange={v => setSrc('microphone', v)}
                masterOff={masterOff}
              />

              {/* Info box */}
              <div style={{
                marginTop: 10,
                padding: '10px 12px',
                background: 'rgba(255,200,0,0.05)',
                border: '1px solid rgba(255,200,0,0.12)',
                borderRadius: 6,
                display: 'flex', gap: 8,
              }}>
                <AlertTriangle size={13} style={{ color: '#ffcc00', flexShrink: 0, marginTop: 1 }} />
                <div style={{ color: '#666', fontSize: 8, lineHeight: 1.6, fontFamily: '"JetBrains Mono", monospace' }}>
                  TPD Calls for Service is the primary data source (~30 min delay). Gemini Search is slower but can surface incidents not yet in the TPD feed. Recommended: TPD ON, Gemini OFF unless you need broader coverage.
                </div>
              </div>
            </div>
          )}

          {/* ══ USERS TAB ════════════════════════════════════════ */}
          {activeTab === 'users' && (
            <div>
              {/* Rules not deployed warning */}
              {rulesDeployed === false && (
                <div style={{
                  padding: '12px', background: 'rgba(255,200,0,0.06)',
                  border: '1px solid rgba(255,200,0,0.2)', borderRadius: 6, marginBottom: 14,
                }}>
                  <div style={{ color: '#ffcc00', fontSize: 9, fontWeight: 700, letterSpacing: 1.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Terminal size={12} /> FIRESTORE RULES NEED DEPLOYMENT
                  </div>
                  <div style={{ color: '#666', fontSize: 8, lineHeight: 1.7, fontFamily: '"JetBrains Mono", monospace' }}>
                    Run on your local machine:<br />
                    <span style={{ color: '#00e5ff' }}>
                      npm install -g firebase-tools<br />
                      firebase login<br />
                      cd /path/to/tucsonlive<br />
                      firebase deploy --only firestore:rules
                    </span>
                    <br />Then refresh this panel.
                  </div>
                </div>
              )}

              {/* ── Whitelist grant ── */}
              <div style={{
                marginBottom: 16, padding: 12,
                background: 'rgba(0,229,255,0.03)',
                border: '1px solid rgba(0,229,255,0.12)',
                borderRadius: 6,
              }}>
                <div style={{ color: '#00e5ff', fontSize: 9, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>
                  🔓 GRANT ACCESS — WHITELIST
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    type="email"
                    value={whitelistEmail}
                    onChange={e => setWhitelistEmail(e.target.value)}
                    placeholder="email@example.com"
                    style={{
                      flex: 1, background: '#0a0a1a', border: '1px solid #222',
                      borderRadius: 4, padding: '5px 8px', color: '#ccc',
                      fontSize: 9, fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  <select
                    value={whitelistTier}
                    onChange={e => setWhitelistTier(e.target.value as any)}
                    style={{
                      background: '#0a0a1a', border: '1px solid #222',
                      borderRadius: 4, padding: '5px 8px', color: '#00e5ff',
                      fontSize: 9, fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
                    }}
                  >
                    <option value="spotter">SPOTTER</option>
                    <option value="tactical">TACTICAL</option>
                    <option value="ghost">GHOST</option>
                  </select>
                </div>
                <button
                  onClick={grantWhitelist}
                  disabled={whitelistPending || !whitelistEmail.trim()}
                  style={{
                    width: '100%', padding: '6px', background: 'rgba(0,229,255,0.1)',
                    border: '1px solid rgba(0,229,255,0.3)', borderRadius: 4,
                    color: '#00e5ff', fontSize: 8, fontWeight: 700, letterSpacing: 1.5,
                    cursor: 'pointer', fontFamily: 'inherit',
                    opacity: whitelistPending || !whitelistEmail.trim() ? 0.4 : 1,
                  }}
                >
                  {whitelistPending ? 'GRANTING...' : 'GRANT ACCESS'}
                </button>
                {whitelistStatus && (
                  <div style={{
                    marginTop: 8, fontSize: 8, fontFamily: '"JetBrains Mono", monospace',
                    color: whitelistStatus.startsWith('✅') ? '#00e676' : '#ff2244',
                  }}>
                    {whitelistStatus}
                  </div>
                )}
              </div>

              {/* Header + refresh */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ color: '#555', fontSize: 9, letterSpacing: 1.5, fontWeight: 700 }}>
                  {userLoading ? 'LOADING...' : `${userList.length} USERS`}
                </span>
                <button
                  onClick={fetchUsers}
                  disabled={userLoading}
                  style={{
                    marginLeft: 'auto', background: 'none', border: '1px solid #222',
                    borderRadius: 4, padding: '4px 8px', color: '#444',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 8, fontFamily: 'inherit',
                  }}
                >
                  <RefreshCw size={10} style={{ animation: userLoading ? 'spin 1s linear infinite' : 'none' }} />
                  REFRESH
                </button>
              </div>

              {userError && userError !== 'RULES_NOT_DEPLOYED' && (
                <div style={{ color: '#ff2244', fontSize: 8, marginBottom: 10, fontFamily: '"JetBrains Mono", monospace' }}>
                  {userError}
                </div>
              )}

              {/* User list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {userList.map(u => (
                  <div key={u.uid} style={{
                    background: u.banned ? 'rgba(255,34,68,0.05)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${u.banned ? 'rgba(255,34,68,0.2)' : u.silenced ? 'rgba(255,200,0,0.15)' : 'rgba(255,255,255,0.05)'}`,
                    borderRadius: 6, overflow: 'hidden',
                  }}>
                    {/* User header row */}
                    <div
                      onClick={() => toggleExpand(u.uid)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px', cursor: 'pointer',
                      }}
                    >
                      {/* Avatar initial */}
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        background: u.banned ? 'rgba(255,34,68,0.2)' : 'rgba(0,229,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: u.banned ? '#ff2244' : '#00e5ff', fontWeight: 700,
                      }}>
                        {(u.displayName || u.email || '?')[0].toUpperCase()}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#ccc', fontSize: 9, fontWeight: 700, marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.displayName || u.email || u.uid.slice(0, 12)}
                        </div>
                        <div style={{ color: '#444', fontSize: 7.5, fontFamily: '"JetBrains Mono", monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.email}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {u.banned && <span style={{ color: '#ff2244', fontSize: 7, fontWeight: 700, letterSpacing: 1 }}>BANNED</span>}
                        {u.silenced && <span style={{ color: '#ffcc00', fontSize: 7, fontWeight: 700, letterSpacing: 1 }}>MUTED</span>}
                        {(u as any).subscription?.tier && (u as any).subscription.tier !== 'free' && (
                          <span style={{
                            color: (u as any).subscription.whitelisted ? '#00e676' : '#00e5ff',
                            fontSize: 7, fontWeight: 700, letterSpacing: 1,
                            border: `1px solid ${(u as any).subscription.whitelisted ? 'rgba(0,230,118,0.3)' : 'rgba(0,229,255,0.3)'}`,
                            borderRadius: 3, padding: '1px 4px',
                          }}>
                            {(u as any).subscription.tier.toUpperCase()}{(u as any).subscription.whitelisted ? ' ★' : ''}
                          </span>
                        )}
                        {u.incidentCount !== undefined && (
                          <span style={{ color: '#444', fontSize: 7.5 }}>{u.incidentCount} rpts</span>
                        )}
                        {u.lastLogin && (
                          <span style={{ color: '#333', fontSize: 7 }}>
                            {new Date(u.lastLogin).toLocaleDateString()}
                          </span>
                        )}
                        {expandedUser === u.uid
                          ? <ChevronDown size={10} style={{ color: '#444' }} />
                          : <ChevronRight size={10} style={{ color: '#333' }} />
                        }
                      </div>
                    </div>

                    {/* Expanded panel */}
                    {expandedUser === u.uid && (
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.04)', padding: '10px 10px' }}>
                        {/* UID */}
                        <div style={{ color: '#333', fontSize: 7.5, fontFamily: '"JetBrains Mono", monospace', marginBottom: 8 }}>
                          {u.uid}
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                          <button
                            onClick={() => moderateUser(u.uid, u.banned ? 'unban' : 'ban')}
                            disabled={actionPending === u.uid + (u.banned ? 'unban' : 'ban')}
                            style={{
                              flex: 1, padding: '6px 4px',
                              background: u.banned ? 'rgba(0,229,255,0.08)' : 'rgba(255,34,68,0.1)',
                              border: `1px solid ${u.banned ? 'rgba(0,229,255,0.2)' : 'rgba(255,34,68,0.3)'}`,
                              borderRadius: 4, color: u.banned ? '#00e5ff' : '#ff2244',
                              fontSize: 8, fontWeight: 700, letterSpacing: 1,
                              cursor: 'pointer', fontFamily: 'inherit',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                            }}
                          >
                            <Ban size={10} />
                            {u.banned ? 'UNBAN' : 'BAN'}
                          </button>
                          <button
                            onClick={() => moderateUser(u.uid, u.silenced ? 'unsilence' : 'silence')}
                            disabled={actionPending === u.uid + (u.silenced ? 'unsilence' : 'silence')}
                            style={{
                              flex: 1, padding: '6px 4px',
                              background: u.silenced ? 'rgba(0,229,255,0.08)' : 'rgba(255,200,0,0.08)',
                              border: `1px solid ${u.silenced ? 'rgba(0,229,255,0.2)' : 'rgba(255,200,0,0.2)'}`,
                              borderRadius: 4, color: u.silenced ? '#00e5ff' : '#ffcc00',
                              fontSize: 8, fontWeight: 700, letterSpacing: 1,
                              cursor: 'pointer', fontFamily: 'inherit',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                            }}
                          >
                            <VolumeX size={10} />
                            {u.silenced ? 'UNMUTE' : 'SILENCE'}
                          </button>
                        </div>

                        {/* Their reports */}
                        <div style={{ color: '#444', fontSize: 8, fontWeight: 700, letterSpacing: 1.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <FileText size={10} />
                          REPORTED INCIDENTS
                        </div>
                        {!userIncidents[u.uid] ? (
                          <div style={{ color: '#333', fontSize: 8, fontFamily: '"JetBrains Mono", monospace' }}>Loading...</div>
                        ) : userIncidents[u.uid].length === 0 ? (
                          <div style={{ color: '#333', fontSize: 8 }}>No reports yet.</div>
                        ) : (
                          <div style={{ maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {userIncidents[u.uid].map((inc: any, i) => (
                              <div key={i} style={{
                                padding: '4px 8px',
                                background: 'rgba(255,255,255,0.02)',
                                border: '1px solid rgba(255,255,255,0.04)',
                                borderRadius: 3,
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                  <span style={{ color: '#00e5ff', fontSize: 8, fontWeight: 700, letterSpacing: 1 }}>{inc.type}</span>
                                  <span style={{ color: '#333', fontSize: 7.5 }}>{inc.timestamp ? new Date(inc.timestamp).toLocaleString() : ''}</span>
                                </div>
                                <div style={{ color: '#555', fontSize: 8, fontFamily: '"JetBrains Mono", monospace', lineHeight: 1.4 }}>
                                  {(inc.description || '').slice(0, 120)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {userList.length === 0 && !userLoading && rulesDeployed !== false && (
                  <div style={{ color: '#333', fontSize: 9, textAlign: 'center', padding: 20 }}>
                    No users found.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══ TIMING TAB ═══════════════════════════════════════ */}
          {activeTab === 'timing' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { key: 'syncInterval',      label: 'Gemini Sync Interval',   unit: 'ms', desc: 'How often to poll Gemini Search (if enabled). Min 60000.' },
                { key: 'vicinityCooldown',  label: 'Vicinity Analysis Cooldown', unit: 'ms', desc: 'Cooldown between neighborhood analysis scans.' },
                { key: 'summarizeCooldown', label: 'Summarize Cooldown',      unit: 'ms', desc: 'Cooldown between dispatch summarization calls.' },
              ].map(f => (
                <div key={f.key} style={{
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: 6, padding: '10px 12px',
                }}>
                  <div style={{ color: '#888', fontSize: 9, fontWeight: 700, letterSpacing: 1.5, marginBottom: 4 }}>
                    {f.label.toUpperCase()}
                  </div>
                  <div style={{ color: '#444', fontSize: 8, marginBottom: 8, fontFamily: '"JetBrains Mono", monospace' }}>
                    {f.desc}
                  </div>
                  <input
                    type="number"
                    value={(settings as any)[f.key]}
                    onChange={e => set(f.key, parseInt(e.target.value))}
                    style={{
                      width: '100%', background: '#0a0a1a', border: '1px solid #222',
                      borderRadius: 4, padding: '6px 10px', color: '#00e5ff',
                      fontSize: 12, fontFamily: '"JetBrains Mono", monospace',
                    }}
                  />
                  <div style={{ color: '#333', fontSize: 7.5, marginTop: 4 }}>
                    = {((settings as any)[f.key] / 1000).toFixed(0)}s
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ══ THEME TAB ════════════════════════════════════════ */}
          {activeTab === 'theme' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { key: 'theme.primary', label: 'Primary' },
                { key: 'theme.danger',  label: 'Danger' },
                { key: 'theme.accent',  label: 'Accent' },
                { key: 'theme.bg',      label: 'Background' },
                { key: 'theme.header',  label: 'Header' },
                { key: 'theme.border',  label: 'Border' },
              ].map(f => (
                <div key={f.key} style={{
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: 6, padding: '10px 12px',
                }}>
                  <div style={{ color: '#555', fontSize: 8, fontWeight: 700, letterSpacing: 1.5, marginBottom: 6 }}>
                    {f.label.toUpperCase()}
                  </div>
                  <input
                    type="color"
                    value={(settings.theme as any)[f.key.split('.')[1]]}
                    onChange={e => set(f.key, e.target.value)}
                    style={{ width: '100%', height: 34, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'none' }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* ══ API KEYS TAB ═════════════════════════════════════ */}
          {activeTab === 'keys' && (
            <div>
              <GeminiSubscriptionManager
                userGeminiKey={userGeminiKey}
                setUserGeminiKey={setUserGeminiKey}
                isPaidSubscriber={isPaidSubscriber}
                setIsPaidSubscriber={setIsPaidSubscriber}
              />
              <div style={{
                marginTop: 14,
                padding: '10px 12px',
                background: 'rgba(0,229,255,0.04)',
                border: '1px solid rgba(0,229,255,0.1)',
                borderRadius: 6,
              }}>
                <div style={{ color: '#00e5ff', fontSize: 9, fontWeight: 700, letterSpacing: 1.5, marginBottom: 6 }}>
                  TPD CALLS FOR SERVICE
                </div>
                <div style={{ color: '#444', fontSize: 8, lineHeight: 1.6, fontFamily: '"JetBrains Mono", monospace' }}>
                  No API key required. Tucson GIS is a public feed — server ingests and caches data automatically.
                </div>
                <div style={{
                  marginTop: 8, display: 'flex', alignItems: 'center', gap: 6,
                  color: '#00e5ff', fontSize: 8,
                }}>
                  <Globe size={10} />
                  gisdata.tucsonaz.gov
                </div>
              </div>

              {/* ── Admin tier override ── */}
              {onTierChange && (
                <div style={{
                  marginTop: 14,
                  padding: '10px 12px',
                  background: 'rgba(147,51,234,0.06)',
                  border: '1px solid rgba(147,51,234,0.25)',
                  borderRadius: 6,
                }}>
                  <div style={{ color: '#c084fc', fontSize: 9, fontWeight: 700, letterSpacing: 1.5, marginBottom: 8 }}>
                    ADMIN TIER OVERRIDE
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(['free', 'spotter', 'tactical', 'ghost'] as const).map(t => (
                      <button
                        key={t}
                        onClick={() => onTierChange(t)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 4,
                          border: `1px solid ${currentTier === t ? '#c084fc' : 'rgba(147,51,234,0.2)'}`,
                          background: currentTier === t ? 'rgba(147,51,234,0.25)' : 'rgba(0,0,0,0.3)',
                          color: currentTier === t ? '#c084fc' : '#555',
                          fontSize: 8, fontWeight: 700, letterSpacing: 1.2,
                          cursor: 'pointer', fontFamily: 'inherit',
                          transition: 'all 0.15s',
                          textTransform: 'uppercase',
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div style={{ color: '#555', fontSize: 7.5, marginTop: 6, fontFamily: '"JetBrains Mono", monospace' }}>
                    Active: <span style={{ color: '#c084fc' }}>{currentTier?.toUpperCase()}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div style={{
          padding: '12px 14px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          background: 'rgba(0,0,0,0.3)',
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '10px',
              background: 'rgba(0,229,255,0.1)',
              border: '1px solid rgba(0,229,255,0.3)',
              borderRadius: 6, color: '#00e5ff',
              fontSize: 9, fontWeight: 700, letterSpacing: 2,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.15s',
            }}
          >
            <Save size={13} />
            SAVE & CLOSE
          </button>
        </div>
      </div>

      <style>{`
        @keyframes dev-pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
    </div>
  );
}
